import { Component } from "react"
import PropTypes from "prop-types"
import Form from "app/components/sdk/form"
import LimitedTextInput from "shared/limited_text_input"
import { gql, graphQueryBang } from "shared/lib/graphql"
import { get, putFile } from "shared/lib/api"
import displayFlash from "partners/scripts/display_flash"
import InlineErrors from "shared/components/inline_errors"
import alertUnsavedHOC from "shared/components/alert_unsaved_hoc"
import AppletConfigurations from "./applet_configurations"
import { appletConfigShape } from "./applet_configuration"
import SelectedServiceView from "./selected_service_view/index"
import { loadAvailableTandasUnified } from "./tanda_gql"
import OpenLinksInNewWindow from "shared/components/open_links_in_new_window"
import { del } from "shared/lib/api"
import ConnectionScriptingEditor from "./connection_scripting_editor"
import classnames from "classnames"
import isEqual from "lodash/isEqual"
import { capitalize } from "humanize-plus"

import styles from "./dynamic_applet.module.scss"

const IMAGE_HEIGHT = 2160
const IMAGE_WIDTH = 4320

export class Review extends Component {
  constructor(props) {
    super(props)
    const backgroundImages = this.props.applet.background_images || {}

    this.state = {
      submitting: false,
      webhook_test_successful: undefined,
      errors: {},
      name: this.props.applet.name || "",
      description: this.props.applet.description || "",
      raw_code:
        (this.props.applet.applet_script &&
          this.props.applet.applet_script.raw_code) ||
        "",
      backgroundUrl: backgroundImages.background_image_url_1x,
      backgroundThumbnailUrl: backgroundImages.sm_background_image_url,
      stagingBackgroundUploadedAt: null,
      availableTandas: {},
      appletConfigurations: [],
      tandas_updated: false,
    }
  }

  componentDidMount() {
    loadAvailableTandasUnified(
      [...this.props.applet.channels, this.props.applet.owner_channel].map(
        s => s.module_name
      )
    ).then(availableTandas => {
      // normalize trigger/action shape
      const tandaShape = (tanda, type, index) => ({
        ...tanda,
        full_module_name: tanda[`${type}`].full_module_name,
        indexByType: index,
        type,
        name: tanda[`${type}`].name,
        channel: {
          id: availableTandas[tanda.channel_module_name].id,
        },
        fields: tanda.fields.map(f => ({
          name: f.name,
          custom_label: f.label || "",
          default_value: f.default_value_json
            ? JSON.parse(f.default_value_json)
            : null,
          hidden: f.hidden,
        })),
        collapsed: true,
      })
      const appletConfigurations = []
      this.props.applet.applet_configurations.forEach(ac => {
        const actions = []
        ac.applet_actions.forEach((a, i) => {
          actions.push(tandaShape(a, "action", i))
        })
        const queries = []
        ac.applet_queries.forEach((q, i) => {
          queries.push(tandaShape(q, "query", i))
        })
        const triggers = []
        ac.applet_triggers.forEach((t, i) => {
          // Find ingredients for trigger to be used on Runtime Script
          const ingredients = availableTandas.triggers.find(
            available_trigger =>
              available_trigger.channel_module_name === t.channel_module_name &&
              available_trigger.module_name === t.module_name
          ).ingredients
          triggers.push(tandaShape({ ...t, ingredients }, "trigger", i))
        })
        const customFields = (ac.customFields || []).map(cf => {
          cf.defaultValue = JSON.parse(cf.defaultValueJson || null)
          cf.collapsed = true
          return cf
        })
        const { description, icon, id, name, title, required } = ac
        appletConfigurations.push({
          description,
          icon,
          id,
          name,
          title,
          required,
          triggers,
          queries,
          actions,
          customFields,
          status: "valid",
        })
      })
      this.setState({ availableTandas, appletConfigurations })
    })
  }

  componentDidUpdate(_, prevState) {
    let elToScrollTo = null
    if (!isEqual(prevState.errors, this.state.errors)) {
      elToScrollTo =
        this.reviewRef.querySelector(".inline-errors") ||
        this.reviewRef.querySelector(
          "[class^='applet_configuration__created-features']"
        )
    }

    if (elToScrollTo) {
      const elementRect = elToScrollTo.getBoundingClientRect()
      const absoluteElementTop = elementRect.top + window.pageYOffset
      const middle = absoluteElementTop - window.innerHeight / 2
      window.scrollTo(0, middle)
    }
  }

  handleErrors = (errors, dryRun) => {
    const errorsToHash = errors.reduce((acc, cur) => {
      if (cur.attribute in acc) {
        acc[cur.attribute] = acc[cur.attribute].concat([cur.message])
      } else {
        acc[cur.attribute] = [cur.message]
      }
      return acc
    }, {})
    const resetACs = this.resetAppletConfigurationsValidationStatus("valid")
    if (errors && errors.length > 0) {
      this.setState({
        errors: errorsToHash,
        appletConfigurations: resetACs,
      })
      if (!dryRun) throw errors
    } else {
      this.setState({ errors: {}, appletConfigurations: resetACs })
    }
  }

  handleInputChange = name => {
    return value => this.setState({ [name]: value })
  }

  fulfillUpdateRequirements = (publishing, clearedConfigs, dryRun = false) => {
    const { name, appletConfigurations } = this.state
    let errors = {}
    if (!dryRun && name == "") {
      errors.name = ["Title can't be blank"]
    }
    if (clearedConfigs.some(config => config.title.length < 5)) {
      errors.featureTitles = ["Feature titles must be at least 5 characters"]
    }

    if (publishing) {
      if (appletConfigurations.length < 1) {
        errors.minimum_applet_configurations = [
          "Must include at least 1 feature",
        ]
      }
    }
    this.setState({ errors })
    if (Object.keys(errors).length > 0) {
      displayFlash(
        `Could not ${this.actionForMode(
          this.props.mode
        )} connection — missing requirement`,
        "error"
      )
      return false
    }
    return true
  }

  filterFields(tanda) {
    return {
      module_name: tanda.module_name,
      channel_id: parseInt(
        tanda.channel ? tanda.channel.id : tanda.service.id,
        10
      ),
      fields: tanda.fields.map(field => {
        if (field.default_value && typeof field.default_value === "object") {
          return {
            name: field.name,
            custom_label: field.custom_label,
            default_value_json: JSON.stringify(field.default_value),
            hidden: field.hidden,
          }
        }
        return field
      }),
    }
  }

  formatCustomFieldForMutation(cf) {
    const formattedField = {
      id: cf.id,
      name: cf.name,
      label: cf.label,
      field_ui_type: cf.fieldUiType,
      required: cf.required,
      helper_text: cf.helperText,
    }
    switch (cf.fieldUiType) {
      case "text_field":
      case "collection_select":
      case "time_select":
        // Primitive types cannot be updated using default_value_json
        formattedField.default_value = cf.defaultValue
        break
      default:
        formattedField.default_value_json = JSON.stringify(cf.defaultValue)
    }
    if (cf.options) {
      formattedField.static_options = cf.options.map(o => {
        return {
          label: o.label,
          value: o.value,
        }
      })
    }
    return formattedField
  }

  handleSubmit = (event, publishing, dryRun = false, index) => {
    if (event) event.preventDefault()

    const clearedConfigs = this.state.appletConfigurations.filter(
      c => c.removed !== true
    )

    this.setState(
      {
        appletConfigurations: clearedConfigs,
      },
      () => {
        if (
          !this.fulfillUpdateRequirements(publishing, clearedConfigs, dryRun)
        ) {
          const determinedACs = this.resetAppletConfigurationsValidationStatus(
            "dirty"
          )
          this.setState({ appletConfigurations: determinedACs })
          return
        }
        const {
          preview_url,
          service: { slug: serviceSlug },
          selectedService: { slug: selectedServiceSlug },
        } = this.props

        this.setState({ submitting: true })

        const {
          description,
          stagingBackgroundUploadedAt,
          raw_code,
          appletConfigurations,
        } = this.state

        // only do single dryRun dac validation if they have already been saved before
        // otherwise IFE will not know which dac we are refering to
        const configsForMutation =
          dryRun && index && appletConfigurations[index].name
            ? [appletConfigurations[index]]
            : appletConfigurations

        // During dryRun (feature validation), avoid validation fails for connection name when user doesn't fill yet.
        const name = dryRun ? this.state.name || "anything" : this.state.name

        graphQueryBang(Review.DynamicAppletUpdateMutation, {
          id: this.props.applet.numeric_id || "new",
          dry_run: dryRun,
          owner_module_name: serviceSlug,
          connected_channel_module_names: [selectedServiceSlug],
          name,
          description,
          background_uploaded_at: stagingBackgroundUploadedAt,
          applet_script: { raw_code },
          applet_configurations: configsForMutation.map(ac => ({
            name: ac.name || null,
            description: ac.description,
            icon: ac.icon,
            title: ac.title,
            required: ac.required,
            dynamic_applet_triggers: ac.triggers.map(step =>
              this.filterFields(step)
            ),
            dynamic_applet_queries: ac.queries.map(step =>
              this.filterFields(step)
            ),
            dynamic_applet_actions: ac.actions.map(step =>
              this.filterFields(step)
            ),
            custom_fields: ac.customFields.map(cf =>
              this.formatCustomFieldForMutation(cf)
            ),
          })),
          published: publishing ? true : null,
        })
          .then(({ dynamicAppletUpdate }) => {
            this.handleErrors(dynamicAppletUpdate.errors, dryRun)
            if (!dryRun) {
              displayFlash("Changes saved")
              del(this.props.clear_cache_url)

              if (this.props.applet.id) {
                window.location.assign(preview_url)
              } else {
                window.location.assign(
                  `${preview_url}${dynamicAppletUpdate.applet.id}`
                )
              }
            }
          })
          .catch(() => {
            // We got here while validating a configuration.
            // We don't have specific errors for this configuration
            // but we know it's the one that was in "validating" state.
            // So we just mark any "validating" configuration as "dirty" here.
            const resetACs = this.resetAppletConfigurationsValidationStatus(
              "dirty"
            )
            this.setState({ appletConfigurations: resetACs })
            displayFlash(
              "Could not update connection — please check your data",
              "error"
            )
          })
          .finally(() => this.setState({ submitting: false }))
      }
    )
  }

  resetAppletConfigurationsValidationStatus = newStatus => {
    return this.state.appletConfigurations.map(ac => ({
      ...ac,
      status: ac.status === "validating" ? newStatus : ac.status,
    }))
  }

  uploadFile = file => {
    // s3_signing_url already has `?selected_service=service_id` query param
    let signingURL = new URL(this.props.s3_signing_url)
    signingURL.searchParams.set("image_content_type", file.type)

    return get(signingURL.href)
      .then(res => {
        return putFile(res.signedUrl, file).then(() => res)
      })
      .then(({ backgroundUrl, backgroundThumbnailUrl, uploadedAt }) => {
        this.setState({
          backgroundUrl,
          backgroundThumbnailUrl,
          stagingBackgroundUploadedAt: uploadedAt,
        })
      })
      .catch(() =>
        displayFlash("Could not upload image — please try again later", "error")
      )
  }

  handleFileChange = e => {
    const file = e.target.files[0]
    if (file && ["image/png", "image/jpeg"].includes(file.type)) {
      const image = new Image()
      image.onload = function (e) {
        if (
          e.target.naturalHeight == IMAGE_HEIGHT &&
          e.target.naturalWidth == IMAGE_WIDTH
        ) {
          this.uploadFile(file)
        } else {
          displayFlash(
            `Your image must be ${IMAGE_WIDTH}px x ${IMAGE_HEIGHT}px`,
            "error"
          )
        }
      }.bind(this)
      image.src = URL.createObjectURL(file)
    } else {
      displayFlash("Your image must be a PNG or JPEG", "error")
    }
  }

  handleFileRemoveClick = e => {
    e.preventDefault()
    this.setState({
      stagingBackgroundUploadedAt: "",
      backgroundUrl: null,
    })
  }

  handleAppletConfigChange = (appletConfigurations, indexToValidate = null) => {
    const { errors } = this.state
    delete errors.featureTitles
    this.setState(
      { appletConfigurations, tandas_updated: true, errors },
      () => {
        if (indexToValidate !== null) {
          this.handleSubmit(null, false, true, indexToValidate)
        }
      }
    )
  }

  actionForMode = mode => {
    return mode === "edit" ? "update" : mode === "new" ? "create" : "publish"
  }

  render() {
    const {
      submitting,
      appletConfigurations,
      availableTandas,
      errors,
    } = this.state

    const {
      contextChannelId,
      mode,
      applet: { id: appletID },
      service,
      selectedService,
      urls,
      value_prop_icons,
    } = this.props

    return (
      <OpenLinksInNewWindow>
        <section className={`${styles.dynamic_applet} ${styles.review}`} ref={ref => (this.reviewRef = ref)}>
          <h1>{capitalize(mode)} connection</h1>

          <div className="form applet-composer programmable-connection applet-composer-with-right-gutter">
            <SelectedServiceView
              service={service}
              selectedService={selectedService}
              urls={urls}
              showRemoveLink={!appletID}
            />
            <div className={styles["feature-section-header"]}>
              <h2>Features</h2>
            </div>
            <AppletConfigurations
              contextChannelId={contextChannelId}
              configurations={appletConfigurations}
              onAppletConfigsChange={this.handleAppletConfigChange}
              availableTandas={availableTandas}
              errors={errors}
              icons={value_prop_icons}
              appletUpdating={submitting}
              urls={urls}
            />
            <InlineErrors
              errors={
                errors.minimum_applet_configurations ||
                errors.connected_channel_module_names ||
                errors.base ||
                errors.featureTitles
              }
            />
          </div>

          <div className={classnames("form", styles["form"])}>
            <Form>
              <h2>Design your connection</h2>
              <p>
                Provide details on the experience that you are creating for users. The title, description and value
                propositions will be shown to users looking to enable this connection on IFTTT. It is recommended that
                you upload a cover image to make your connection stand out.
              </p>

              <div className={styles["design"]}>
                <div className={styles["design-column"]}>
                  <div className="field">
                    <div className={classnames("label", styles["label"])}>Connection title</div>
                    <LimitedTextInput
                      value={this.state.name}
                      onChange={this.handleInputChange("name")}
                      limit={80}
                      multiline={false}
                      counterPosition="top"
                      errors={this.state.errors.name}
                      hardLimit
                    />
                    <p className="note">Use sentence case capitalization without a period.</p>
                  </div>
                  <div className="field">
                    <div className={classnames("label", styles["label"])}>Description</div>
                    <LimitedTextInput
                      value={this.state.description}
                      onChange={this.handleInputChange("description")}
                      limit={500}
                      minRows={3}
                      counterPosition="top"
                      errors={this.state.errors.description}
                      multiline
                      hardLimit
                    />
                  </div>
                  <section className="form-section">
                    <div className="field">
                      <h3>Cover image</h3>
                      <p>
                        Cover image must be a PNG or JPG file that is {IMAGE_WIDTH}px x {IMAGE_HEIGHT}px. For best
                        results, make sure that the image is not bright.
                        <br />
                        <br />
                        Download the connection cover image template for{" "}
                        <a
                          className="link-inline"
                          href="/partners/templates/IFTTT_Connection_Cover_Image-4320x2160_sketch.zip"
                          rel="noopener noreferrer"
                        >
                          Sketch
                        </a>
                        ,{" "}
                        <a
                          className="link-inline"
                          href="/partners/templates/IFTTT_Connection_Cover_Image-4320x2160_illustrator.zip"
                          rel="noopener noreferrer"
                        >
                          Illustrator
                        </a>
                        , or{" "}
                        <a
                          className="link-inline"
                          href="/partners/templates/IFTTT_Connection_Cover_Image-4320x2160_photoshop.zip"
                          rel="noopener noreferrer"
                        >
                          Photoshop
                        </a>
                      </p>
                      {this.state.backgroundUrl ? (
                        <div className={styles["file-handler"]}>
                          <div>
                            <img src={this.state.backgroundThumbnailUrl} />
                          </div>
                          <a href="" onClick={this.handleFileRemoveClick}>
                            Remove
                          </a>
                        </div>
                      ) : (
                        <>
                          <label
                            className={classnames("button-tertiary button-outlined", {
                              [styles["applet-background-upload-disabled"]]: submitting,
                            })}
                            htmlFor="applet-background-upload"
                          >
                            Upload Image
                          </label>
                          <input
                            id="applet-background-upload"
                            disabled={submitting}
                            style={{ visibility: "hidden" }}
                            type="file"
                            onClick={event => {
                              event.target.value = ""
                            }}
                            onChange={this.handleFileChange}
                            accept="image/png,image/jpeg"
                          />
                        </>
                      )}
                    </div>
                  </section>
                  <div className={classnames("field", styles["runtime-script-container"])}>
                    <h2>Runtime script</h2>
                    <p>
                      This runtime script will run anytime there is a new trigger event for this connection. Within a
                      runtime script, you can perform queries and run actions.
                    </p>
                    <p>Example of performing a query and running an action:</p>
                    <code>
                      <pre>
                        {`// update service_id.query_id and
// service_id.action_id to the
// queries and/or actions you've
// configured in this connection

performQuery("service_id.query_id").then(
  (query) => {
    return runAction("service_id.action_id", {
      fields: {
        field1: \`Query: \${JSON.stringify(
          query.responseBody.data[0]
        )}\`,
      }
    }
  );
});
`}
                      </pre>
                    </code>

                    <div className={classnames("applet-composer applet-composer-with-right-gutter", styles.design)}>
                      <ConnectionScriptingEditor
                        contextTemplate={this.props.scriptingTransformTemplate}
                        contextData={{ services: [], trigger: {} }}
                        configurations={appletConfigurations}
                        runtimeScriptDocUrl={this.props.urls.runtimeScriptDocUrl}
                        scriptCode={this.state.raw_code}
                        onChange={this.handleInputChange("raw_code")}
                        errors={this.state.errors["applet/applet_script"]}
                      />
                    </div>
                  </div>
                </div>
              </div>
              <div className="actions">
                <div className="action-right">
                  <a
                    role="button"
                    href={this.props.preview_url}
                    onClick={this.confirmDelete}
                    className="button-secondary button-cancel-link"
                    data-open-in-current-window={true}
                  >
                    Cancel
                  </a>
                  {mode === "publish" && (
                    <>
                      <button
                        type="submit"
                        className="button-tertiary button-outlined"
                        onClick={event => {
                          this.handleSubmit(event, false)
                        }}
                        disabled={submitting}
                      >
                        {submitting ? "Saving..." : "Save without publishing"}
                      </button>
                      &nbsp;&nbsp;
                    </>
                  )}
                  <button
                    type="submit"
                    className="button-primary"
                    onClick={event => {
                      event.preventDefault()
                      let doSubmit = true
                      if (this.state.tandas_updated && this.props.applet.installs_count > 0) {
                        doSubmit = confirm(
                          "Updating triggers, queries, or actions may cause data inconsistencies. Users that already have this connection enabled will be missing any new required fields. Continue?"
                        )
                      }
                      doSubmit && this.handleSubmit(event, mode === "publish")
                    }}
                    disabled={submitting}
                  >
                    {submitting ? "Saving..." : capitalize(this.actionForMode(mode))}
                  </button>
                </div>
              </div>
            </Form>
          </div>
        </section>
      </OpenLinksInNewWindow>
    )
  }
}

Review.propTypes = {
  applet: PropTypes.shape({
    id: PropTypes.string,
    numeric_id: PropTypes.string,
    name: PropTypes.string,
    description: PropTypes.string,
    background_url: PropTypes.string,
    published: PropTypes.bool.isRequired,
    owner_channel: PropTypes.object.isRequired,
    channels: PropTypes.array.isRequired,
    normalized_applet: PropTypes.shape({
      channels: PropTypes.array.isRequired,
    }),
    value_propositions: PropTypes.arrayOf(
      PropTypes.shape({
        benefit: PropTypes.string.isRequired,
        icon: PropTypes.string.isRequired,
        order: PropTypes.number.isRequired,
      })
    ).isRequired,
    background_images: PropTypes.shape({
      sm_background_image_url: PropTypes.string,
      background_image_url_1x: PropTypes.string,
    }),
    post_configuration: PropTypes.shape({
      message: PropTypes.string.isRequired,
      cta_url: PropTypes.string.isRequired,
    }),
    applet_script: PropTypes.shape({
      raw_code: PropTypes.string,
    }),
    applet_configurations: PropTypes.arrayOf(appletConfigShape),
    installs_count: PropTypes.number,
  }).isRequired,
  contextChannelId: PropTypes.string.isRequired,
  service: PropTypes.object,
  selectedService: PropTypes.object,
  value_prop_icons: PropTypes.array.isRequired,
  s3_signing_url: PropTypes.string.isRequired,
  preview_url: PropTypes.string.isRequired,
  runtimeScriptDocUrl: PropTypes.string.isRequired,
  clear_cache_url: PropTypes.string.isRequired,
  mode: PropTypes.oneOf(["new", "edit", "publish"]),
  scriptingTransformTemplate: PropTypes.string.isRequired,
  urls: PropTypes.object.isRequired,
}

Review.DynamicAppletUpdateMutation = gql`
  mutation DynamicAppletUpdateMutation(
    $id: ID!
    $dry_run: Boolean
    $owner_module_name: String
    $connected_channel_module_names: [String]
    $published: Boolean
    $name: String
    $description: String
    $background_uploaded_at: String
    $applet_configurations: [AppletConfigurationInput]
    $applet_script: AppletScriptInput
    $post_configuration_message: String
    $post_configuration_cta_url: String
  ) {
    dynamicAppletUpdate(
      input: {
        id: $id
        dry_run: $dry_run
        owner_module_name: $owner_module_name
        connected_channel_module_names: $connected_channel_module_names
        published: $published
        name: $name
        description: $description
        background_uploaded_at: $background_uploaded_at
        applet_configurations: $applet_configurations
        applet_script: $applet_script
        post_configuration_message: $post_configuration_message
        post_configuration_cta_url: $post_configuration_cta_url
      }
    ) {
      applet {
        id
      }
      errors {
        attribute
        message
      }
    }
  }
`

Review.DynamicAppletWebhookTestQuery = gql`
  query DynamicAppletWebhookTestQuery($applet_id: String!) {
    applet(id: $applet_id) {
      connection_enabled_webhook_test_status
    }
  }
`

export default alertUnsavedHOC(Review)
